import { Injectable } from "@angular/core";
import { Observable, ReplaySubject, Subject } from "rxjs";
import { map } from "rxjs/internal/operators";
import { ApiResourceService } from "../../apiResource/apiResource.service";

export interface IHostAlias {
    /** The host code */
    host: string;
    /** The displayable host alias. */
    alias: string;
}

export interface IHostCodesAndAliases {
    codes: string[];
    aliases: string [];
}

@Injectable({providedIn: "root"})
export class HostAliasResource {

    private aliasesSubject: Subject<IHostAlias[]>;

    constructor(
        private readonly apiResourceService: ApiResourceService
    ) {
    }

    public getAllAliases(): Observable<IHostAlias[]> {
        return this.refreshAliases();
    }

    public getAlias(host?: string): Observable<string> {
        return this.refreshAliases().pipe(
            map((response: IHostAlias[]): string => {
                const aliasFromCache: string = this.findAliasFromList(response, host);
                return aliasFromCache;
            })
        );
    }

    public getCodesAndAliases(hostCodes: string[]): Observable<IHostCodesAndAliases> {
        return this.refreshAliases().pipe(
            map((hostAliases: IHostAlias[]): IHostCodesAndAliases => (
                {
                    codes: hostCodes,
                    aliases: hostCodes.map((hostCode: string) =>
                        this.findAliasFromList(hostAliases, hostCode))
                }))
        );
    }

    public getAliases(hostCodes: string[]): Observable<IHostAlias[]> {
        return this.refreshAliases().pipe(
            map((hostAliases: IHostAlias[]): IHostAlias[] =>
                (hostCodes || [])
                    .map((hostCode: string): IHostAlias => ({host: hostCode, alias: this.findAliasFromList(hostAliases, hostCode)}))
                    .sort((a: IHostAlias, b: IHostAlias) => a.alias.localeCompare(b.alias)))
        );
    }

    public findAliasFromList(aliases: IHostAlias[], host?: string): string {
        if (!aliases || !host) {
            return null;
        }
        const entry: IHostAlias | null = aliases.find(
            (alias: IHostAlias): boolean => alias.host === host
        );
        return entry ? entry.alias : host;
    }

    private refreshAliases(): Observable<IHostAlias[]> {
        if (!this.aliasesSubject) {
            this.aliasesSubject = new ReplaySubject<IHostAlias[]>();
            this.apiResourceService.get(
                this.apiResourceService.getApiUrl('dictionary', 'dictionaries', 'host', 'aliases')
            ).subscribe(this.aliasesSubject);
        }
        return this.aliasesSubject.asObservable();
    }
}
